import { useCallback, useMemo } from 'react'

import { formOptions, useStore } from '@tanstack/react-form'
import { AIChatLauncherPosition } from 'types/graphql'

import { useUpdateOrganizationMutation } from 'src/fetch/organizations'
import { useAppForm } from 'src/hooks/use-app-form'
import { useMyOrg, useRefetchAppState } from 'src/layouts/AppStateContextLayout/utils'

export type AILauncherSettingsForm = {
  allowListUrls: string[]
  bottomSpacingPx: number | null | undefined
  position: AIChatLauncherPosition | null | undefined
  sideSpacingPx: number | null | undefined
  popupEnabled: boolean
  popupTitle: string
  popupDescription: string
}
export const aiLauncherSettingsFormDefaultValues: AILauncherSettingsForm = {
  allowListUrls: [],
  bottomSpacingPx: 40,
  position: 'BottomRight',
  sideSpacingPx: 40,
  popupEnabled: true,
  popupTitle: 'Questions?',
  popupDescription: 'Ask the Community Assistant',
}
export const aiLauncherSettingsFormOptions = formOptions({
  defaultValues: aiLauncherSettingsFormDefaultValues,
})

export const useLauncherSettingsForm = () => {
  const organization = useMyOrg()
  const { mutateAsync: updateOrganization } = useUpdateOrganizationMutation()
  const refetchAppState = useRefetchAppState()
  const handleSubmit = useCallback(
    async ({ value }: { value: AILauncherSettingsForm }) => {
      await updateOrganization({
        input: {
          aiChatLauncherUrlAllowList: value.allowListUrls,
          aiChatLauncherBottomSpacingPx: value.bottomSpacingPx,
          aiChatLauncherPosition: value.position,
          aiChatLauncherSideSpacingPx: value.sideSpacingPx,
          aiChatLauncherPopupEnabled: value.popupEnabled,
          aiChatLauncherPopupTitle: value.popupTitle,
          aiChatLauncherPopupDescription: value.popupDescription,
        },
      })
      await refetchAppState?.()
    },
    [refetchAppState, updateOrganization]
  )

  const {
    aiChatLauncherUrlAllowList,
    aiChatLauncherBottomSpacingPx,
    aiChatLauncherPosition,
    aiChatLauncherSideSpacingPx,
    aiChatLauncherPopupEnabled,
    aiChatLauncherPopupTitle,
    aiChatLauncherPopupDescription,
  } = organization ?? {}
  const defaultValues: AILauncherSettingsForm = useMemo(
    () => ({
      allowListUrls:
        aiChatLauncherUrlAllowList ?? aiLauncherSettingsFormDefaultValues.allowListUrls,
      bottomSpacingPx:
        aiChatLauncherBottomSpacingPx ?? aiLauncherSettingsFormDefaultValues.bottomSpacingPx,
      position: aiChatLauncherPosition ?? aiLauncherSettingsFormDefaultValues.position,
      sideSpacingPx:
        aiChatLauncherSideSpacingPx ?? aiLauncherSettingsFormDefaultValues.sideSpacingPx,
      popupEnabled: aiChatLauncherPopupEnabled ?? aiLauncherSettingsFormDefaultValues.popupEnabled,
      popupTitle: aiChatLauncherPopupTitle ?? aiLauncherSettingsFormDefaultValues.popupTitle,
      popupDescription:
        aiChatLauncherPopupDescription ?? aiLauncherSettingsFormDefaultValues.popupDescription,
    }),
    [
      aiChatLauncherBottomSpacingPx,
      aiChatLauncherPosition,
      aiChatLauncherSideSpacingPx,
      aiChatLauncherUrlAllowList,
      aiChatLauncherPopupEnabled,
      aiChatLauncherPopupTitle,
      aiChatLauncherPopupDescription,
    ]
  )
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      await handleSubmit({ value })
      await refetchAppState?.()
      form.reset()
    },
  })

  useStore(form.store) // Listen for changes
  const handleDiscardChanges = useCallback(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  return {
    form,
    onDiscardChanges: handleDiscardChanges,
    onSave: form.handleSubmit,
  }
}
